import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { useSession } from "../core/session/session";
import { useFetch } from "../hooks/useFetch";
import { useNotificationsByUserUnread } from "../hooks/services/useNotification";
import type {
	NotificationResponse,
	UpdateNotificationPayload,
} from "../core/types/services/notifications";
import { connectNotificationsSocket } from "../core/socket/notificationsSocketClient";

export type AppNotification = {
	id: string;
	apiId: number | null;
	title: string;
	message: string;
	createdAt: string;
	isRead: boolean;
};

type NotificationsContextValue = {
	notifications: AppNotification[];
	unreadCount: number;
	markAsRead: (id: string) => void;
	markAllAsRead: () => void;
	refresh: () => Promise<void>;
	isSocketReady: boolean;
	loading: boolean;
};

const NOTIFICATION_EVENTS = [
	"solicitud_aceptada",
	"solicitud_rechazada",
	"servicio_aceptado",
	"cotizacion_recibida",
	"nuevo_mensaje",
	"estado_cambiado",
	"servicio_finalizado",
	"soporte_activado",
	"nueva_solicitud_proveedor",
	"cotizacion_rechazada_proveedor",
	"pedido_rechazado_proveedor",
	"pago_registrado_proveedor",
	"gerente_crud",
	"pedido_en_soporte_soporte",
	"pedido_en_garantia_soporte",
] as const;

function normalizePayloadArray(raw: unknown): NotificationResponse[] {
	if (Array.isArray(raw)) {
		return raw as NotificationResponse[];
	}
	if (!raw || typeof raw !== "object") return [];
	const candidate = raw as Record<string, unknown>;
	if (Array.isArray(candidate.items)) return candidate.items as NotificationResponse[];
	if (Array.isArray(candidate.data)) return candidate.data as NotificationResponse[];
	return [];
}

function formatRelativeTime(value?: string): string {
	if (!value) return "Ahora";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "Ahora";
	const diffMs = Date.now() - date.getTime();
	const diffMin = Math.max(1, Math.floor(diffMs / 60000));
	if (diffMin < 60) return `Hace ${diffMin} min`;
	const diffHours = Math.floor(diffMin / 60);
	if (diffHours < 24) return `Hace ${diffHours} h`;
	const diffDays = Math.floor(diffHours / 24);
	return `Hace ${diffDays} d`;
}

function toAppNotification(item: NotificationResponse): AppNotification {
	return {
		id: String(item.id),
		apiId: Number(item.id ?? 0) || null,
		title: String(item.title_notification ?? "Notificación"),
		message: String(item.message_notification ?? ""),
		createdAt: formatRelativeTime(item.created_at),
		isRead: Boolean(item.is_read),
	};
}

const NOTIFICATION_READ_BATCH_SIZE = 12;

async function markNotificationReadOnServer(
	executeUpdateNotification: ReturnType<
		typeof useFetch<NotificationResponse, UpdateNotificationPayload>
	>["execute"],
	notificationId: number,
) {
	if (!Number.isFinite(notificationId) || notificationId <= 0) return;
	await executeUpdateNotification({
		endpoint: `/notifications/${notificationId}/read`,
		method: "PATCH",
	}).catch(() => {});
}

async function markNotificationsReadInBatches(
	executeUpdateNotification: ReturnType<
		typeof useFetch<NotificationResponse, UpdateNotificationPayload>
	>["execute"],
	notificationsToUpdate: AppNotification[],
) {
	for (
		let index = 0;
		index < notificationsToUpdate.length;
		index += NOTIFICATION_READ_BATCH_SIZE
	) {
		const batch = notificationsToUpdate.slice(
			index,
			index + NOTIFICATION_READ_BATCH_SIZE,
		);
		await Promise.allSettled(
			batch.map((notification) =>
				markNotificationReadOnServer(
					executeUpdateNotification,
					notification.apiId ?? Number(notification.id),
				),
			),
		);
	}
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(
	undefined,
);

export function NotificationsProvider({ children }: { children: ReactNode }) {
	const session = useSession();
	const userId = Number(session?.user?.id ?? 0);
	const hasValidSession = Boolean(session?.user?.id) && userId > 0;
	const [notifications, setNotifications] = useState<AppNotification[]>([]);
	const [isSocketReady, setIsSocketReady] = useState(false);
	const refetchTimerRef = useRef<number | null>(null);

	const {
		data: rawNotifications,
		loading,
		execute: executeFetchByUser,
	} = useNotificationsByUserUnread(userId);
	const { execute: executeUpdateNotification } = useFetch<
		NotificationResponse,
		UpdateNotificationPayload
	>({
		endpoint: "/notifications/0",
		method: "PATCH",
		auto: false,
	});

	useEffect(() => {
		if (!hasValidSession) {
			setNotifications([]);
			return;
		}

		const normalized = normalizePayloadArray(rawNotifications).map(toAppNotification);
		setNotifications(normalized);
	}, [hasValidSession, rawNotifications]);

	const refresh = useCallback(async () => {
		if (!hasValidSession) return;
		await executeFetchByUser({ endpoint: `/notifications/user/${userId}/unread` });
	}, [executeFetchByUser, hasValidSession, userId]);

	const scheduleRefresh = useCallback(() => {
		if (!hasValidSession) return;
		if (refetchTimerRef.current) return;
		refetchTimerRef.current = window.setTimeout(() => {
			refetchTimerRef.current = null;
			void refresh().catch(() => {});
		}, 300);
	}, [hasValidSession, refresh]);

	const markAsRead = useCallback(
		async (id: string) => {
			// Optimistic update: remove immediately from unread list
			setNotifications((prev) => prev.filter((n) => n.id !== id));
			const parsedId = Number(id);
			if (parsedId > 0) {
				await markNotificationReadOnServer(executeUpdateNotification, parsedId);
				await refresh();
			}
		},
		[executeUpdateNotification, refresh],
	);

	const markAllAsRead = useCallback(async () => {
		if (!notifications.length) return;
		const toUpdate = [...notifications];
		// Optimistic update: clear list immediately
		setNotifications([]);
		await markNotificationsReadInBatches(executeUpdateNotification, toUpdate);
		await refresh();
	}, [notifications, executeUpdateNotification, refresh]);

	useEffect(() => {
		if (!hasValidSession) {
			setIsSocketReady(false);
			setNotifications([]);
			return;
		}

		const socket = connectNotificationsSocket();

		const registerUser = () => {
			socket.emit("register", { userId });
		};

		const onRegistered = () => {
			setIsSocketReady(true);
		};

		const onRegistrationError = () => {
			setIsSocketReady(false);
		};

		socket.on("connect", registerUser);
		socket.on("registered", onRegistered);
		socket.on("registrationError", onRegistrationError);

		NOTIFICATION_EVENTS.forEach((eventName) => {
			socket.on(eventName, scheduleRefresh);
		});

		if (socket.connected) {
			registerUser();
		}

		return () => {
			socket.off("connect", registerUser);
			socket.off("registered", onRegistered);
			socket.off("registrationError", onRegistrationError);
			NOTIFICATION_EVENTS.forEach((eventName) => {
				socket.off(eventName, scheduleRefresh);
			});
			if (refetchTimerRef.current) {
				window.clearTimeout(refetchTimerRef.current);
				refetchTimerRef.current = null;
			}
		};
	}, [hasValidSession, scheduleRefresh, userId]);

	const unreadCount = useMemo(
		() => notifications.filter((notification) => !notification.isRead).length,
		[notifications],
	);

	const value = useMemo(
		() => ({
			notifications,
			unreadCount,
			markAsRead,
			markAllAsRead,
			refresh,
			isSocketReady,
			loading,
		}),
		[
			notifications,
			unreadCount,
			markAsRead,
			markAllAsRead,
			refresh,
			isSocketReady,
			loading,
		],
	);

	return (
		<NotificationsContext.Provider value={value}>
			{children}
		</NotificationsContext.Provider>
	);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
	const context = useContext(NotificationsContext);
	if (!context) {
		throw new Error("useNotifications must be used within NotificationsProvider");
	}
	return context;
}
